import React, {useContext, useState} from 'react';
import cn from 'classnames';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import OutsideClickHandler from 'react-outside-click-handler/esm/OutsideClickHandler';

import ControlButton from 'components/Interview/OnlineInterview/RightPanel/Participants/Controls/ControlButton';
import Button, {ButtonStyles} from 'components/common/Form/Button';
import Avatar from 'components/Interview/OnlineInterview/RightPanel/Participants/Avatar';

import getUserAvatarURL from 'components/common/getUserAvatarURL';
import {getParticipantsLimit} from '../InterviewTabs/InterviewTab';
import useCustomTooltip from 'hooks/common/useTooltip';

import OnlineInterviewUserStatuses from 'constants/OnlineInterviewUserStatuses';
import {isObserver} from 'constants/UserRoles';

import {WaitingRoomContext} from 'context/OnlineInterview/WaitingRoomContext';
import {OnlineInterviewContext} from 'context/OnlineInterview/OnlineInterviewContext';
import {InterviewStorageContext} from 'context/Interview/storage/InterviewStorageContext';

import style from './WaitingRoom.module.scss';
import participants from '../Participants/Participants.module.scss';
import {BrowserContext} from 'context/OnlineInterview/BrowserContext';
import TickIcon from 'components/common/icons/TickIcon';
import EditIcon from 'components/common/icons/redesign/EditIcon';
import OnlineInterviewMenu from 'components/common/icons/redesign/OnlineInterviewMenu';

import {Username} from '../Participants/Participants';

const WaitingRoomList = ({className, filter, isTab = false}) => {
    const {waitingRoomState} = useContext(WaitingRoomContext);

    return (
        <div className={cn(style.wrapper, participants.participants, className)}>
            {Object.values(waitingRoomState)
                .filter(user => user.connected)
                .filter(filter)
                .map((waiting, index) => (
                    <WaitingRoomUser isTab={isTab} user={waiting} index={index} key={waiting.id} />
                ))}
        </div>
    );
};

const WaitingRoomUser = ({user, index, isTab}) => {
    const {t} = useTranslation();
    const {waitingRoomState, editingName, setEditingName, setName, kickUser} = useContext(WaitingRoomContext);
    const {tooltip, hoverFunctions} = useCustomTooltip(user.displayName, participants.nameTooltip);
    const isEditingName = user.id === editingName.user;
    const onNameChange = value => {
        setEditingName(user.id, value);
    };
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuHovered, setMenuHovered] = useState(false);
    const onEditNameToggle = value => {
        if (value) {
            return setEditingName(user.id, user.displayName);
        }
        setName(user.id, editingName.value);
    };

    return (
        <div key={user.id} className={participants.participantContainer}>
            <div className={participants.participant}>
                <Avatar avatarURL={getUserAvatarURL(user)} connectionTestResult={user.connectionTest} />
                <div className={participants.info}>
                    <div {...hoverFunctions} className={style.changeName}>
                        <Username
                            name={user.displayName || ''}
                            isEditing={isEditingName}
                            onNameChange={onNameChange}
                            nameValue={editingName.value}
                            onEditNameToggle={onEditNameToggle}
                        />
                    </div>
                    {user.displayName && user.displayName.length >= 10 && tooltip}
                    {user.role && <div className={participants.role}>{t(`roles.${user.role.toLowerCase()}`)}</div>}
                </div>
                <div className={style.controls}>
                    <Controls user={user} />
                    {!isTab && (
                        <div className={style.controlStyles}>
                            <ControlButton
                                value={isEditingName}
                                enabledIcon={TickIcon}
                                disabledIcon={EditIcon}
                                changeValue={onEditNameToggle}
                                enableText={t('onlineInterview:tooltips.changeName')}
                                disableText={t('onlineInterview:tooltips.confirmNameChange')}
                                className={style.icon}
                            />
                        </div>
                    )}
                    {!isTab && (
                        <div className={style.controlStyles}>
                            <div className={style.menuContainer}>
                                <OnlineInterviewMenu
                                    className={style.icon}
                                    onClick={() => setMenuOpen(!menuOpen)}
                                    onMouseEnter={() => setMenuHovered(true)}
                                    onMouseLeave={() => setMenuHovered(false)}
                                />
                                {menuOpen && (
                                    <OutsideClickHandler
                                        onOutsideClick={() => {
                                            if (menuHovered) return;
                                            setMenuOpen(false);
                                        }}
                                    >
                                        <div className={style.menu}>
                                            <div className={style.danger} onClick={() => kickUser(user.id)}>
                                                {t('common.remove')}
                                            </div>
                                        </div>
                                    </OutsideClickHandler>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {index < Object.keys(waitingRoomState).length - 1 && (
                <div className={participants.divider}>
                    <div className={participants.avatar} />
                    <div className={participants.rest} />
                </div>
            )}
        </div>
    );
};

export const Controls = ({user, onAdmit, buttonClassName}) => {
    const {t} = useTranslation();
    const {interview} = useContext(InterviewStorageContext);
    const {usersState, timeout} = useContext(OnlineInterviewContext);
    const {browser} = useContext(BrowserContext);
    const {admit, pendingUsers} = useContext(WaitingRoomContext);
    const {sessionId} = useParams();
    if (!interview) return null;
    const session = interview.sessions.find(session => session._id === sessionId);
    if (!session) return null;

    if (OnlineInterviewUserStatuses.isWaiting(user.status)) {
        return <div className={style.notReady}>{t('waitingRoom:notReady')}</div>;
    }

    if (OnlineInterviewUserStatuses.isTimeout(user.status)) {
        return <div className={style.notReady}>{t('waitingRoom:timeout')}</div>;
    }

    const activeUsers = usersState && usersState.users.filter(user => !isObserver(user.role)).length;
    if (activeUsers + timeout.length + pendingUsers.length >= getParticipantsLimit(interview, session)) {
        return <div className={style.notReady}>{t('common.full')}</div>;
    }

    const handleAdmit = event => {
        event.stopPropagation();
        admit(user.id);
        onAdmit();
    };

    return (
        <Button
            text={t('waitingRoom:admit')}
            className={cn(style.admitButton, buttonClassName)}
            btnStyle={!browser.isMobile && ButtonStyles.primary}
            onClick={handleAdmit}
        />
    );
};

WaitingRoomList.defaultProps = {
    children: null,
    filter: () => true,
};

Controls.defaultProps = {
    onAdmit: () => {},
};

export default WaitingRoomList;
